import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { cn } from "@/lib/cn";

/**
 * Dialog primitive — re-implemented as a right-side SlideDrawer
 *
 * Canonical reference: ianfiles/ui-design-system.md §3.7
 *
 * The legacy app distinguishes Dialog (centered, for confirmations / payment
 * flows / file viewers) from SlideDrawer (right-side, for add/edit forms).
 * Roughly 30 pages in this codebase use `<Dialog>` for forms instead of the
 * canonical SlideDrawer.
 *
 * Rather than rewrite every form per-page, we ship "Option A" from the plan:
 * `<Dialog>` itself now renders as a floating right-side drawer. Every
 * existing `<DialogContent>` keeps working without code changes and visually
 * adopts the canonical drawer chrome.
 *
 * Trade-off (called out explicitly so the next person reading this doesn't
 * undo it by accident): the few legitimate centered Dialog use cases —
 * AlertDialog (covered separately), payment flows, file viewers, OTP prompts
 * — also flip to drawers until we migrate them to AlertDialog or the
 * `centered` opt-in. If a page truly needs a centered surface, pass
 * `centered` to `<DialogContent>`.
 *
 * Sizing:
 *   `<DialogContent>` accepts a className. If a `max-w-{token}` is present
 *   (e.g. `max-w-md`, `max-w-2xl`) the drawer width matches. Otherwise the
 *   drawer defaults to `lg` (matches `max-w-lg` — the legacy Dialog default).
 *
 * Suppressions inherited from `<Sheet>`:
 *   - `modal={false}` is forced via `Dialog` root override below, so nested
 *     Combobox/Popover portals don't fight focus.
 *   - `onInteractOutside` / `onPointerDownOutside` / `onFocusOutside` /
 *     `onOpenAutoFocus` are pre-prevented so users don't lose form state to a
 *     stray outside click. Use `DialogClose` or the X button to dismiss.
 */

// ─── Root + plumbing ────────────────────────────────────────────────────────
const Dialog: React.FC<
  React.ComponentProps<typeof DialogPrimitive.Root>
> = ({ children, ...props }) => (
  // `modal={false}` is the canonical behaviour (see §2.7 / §3.7). Pages can
  // still pass `modal` to opt back in for confirmation-style dialogs.
  <DialogPrimitive.Root modal={false} {...props}>
    {children}
  </DialogPrimitive.Root>
);

const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

// ─── Overlay ────────────────────────────────────────────────────────────────
const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/40 backdrop-blur-[1px]",
      "data-[state=open]:animate-in data-[state=closed]:animate-out",
      "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

// ─── Content ────────────────────────────────────────────────────────────────

/** Parse the first `max-w-*` token from a className, if any. */
function readMaxWidthToken(className: string | undefined): string | null {
  if (!className) return null;
  const match = className.match(/\bmax-w-(\S+)/);
  return match ? match[1] : null;
}

/** Map a `max-w-*` token to a `sm:max-w-*` width class. */
function widthFromToken(token: string | null): string {
  switch (token) {
    case "xs":
      return "sm:max-w-xs";
    case "sm":
      return "sm:max-w-sm";
    case "md":
      return "sm:max-w-md";
    case "lg":
      return "sm:max-w-lg";
    case "xl":
      return "sm:max-w-xl";
    case "2xl":
      return "sm:max-w-2xl";
    case "3xl":
      return "sm:max-w-3xl";
    case "4xl":
      return "sm:max-w-4xl";
    case "5xl":
      return "sm:max-w-5xl";
    case "6xl":
      return "sm:max-w-6xl";
    case "7xl":
      return "sm:max-w-7xl";
    default:
      // Legacy default for `<Dialog>` was `max-w-lg`. Keep that when the
      // caller didn't pass an explicit size.
      return "sm:max-w-lg";
  }
}

export interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  /**
   * Opt back into the original centered-modal layout. Use this only for the
   * narrow set of cases where centered chrome is genuinely appropriate
   * (OTP, payment confirm, file viewer). Most forms should NOT pass this.
   */
  centered?: boolean;
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    {
      className,
      children,
      centered = false,
      onInteractOutside,
      onPointerDownOutside,
      onFocusOutside,
      onOpenAutoFocus,
      ...props
    },
    ref
  ) => {
    const widthClass = widthFromToken(readMaxWidthToken(className));

    // Strip any caller-provided `max-w-*` token from className — we apply it
    // ourselves via `widthClass` at the `sm:` breakpoint so mobile stays
    // full-width but desktop matches the caller's intent.
    const cleanedClassName = className?.replace(/\bmax-w-\S+\s*/g, "");

    return (
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          ref={ref}
          // Suppress outside-click + auto-focus so nested Combobox/Popover
          // portals don't fight the drawer (see Sheet primitive notes).
          onInteractOutside={(e) => {
            e.preventDefault();
            onInteractOutside?.(e);
          }}
          onPointerDownOutside={(e) => {
            e.preventDefault();
            onPointerDownOutside?.(e);
          }}
          onFocusOutside={(e) => {
            e.preventDefault();
            onFocusOutside?.(e);
          }}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            onOpenAutoFocus?.(e);
          }}
          className={cn(
            // Common
            "fixed z-50 flex flex-col gap-4 bg-background text-foreground shadow-lg",
            "transition ease-in-out",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:duration-200 data-[state=open]:duration-300",
            // Layout: centered modal vs floating right drawer
            centered
              ? [
                  "left-[50%] top-[50%] -translate-x-1/2 -translate-y-1/2",
                  "w-full p-6 border sm:rounded-lg",
                  "max-h-[85vh] overflow-y-auto",
                  widthClass,
                  "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
                  "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
                ].join(" ")
              : [
                  "right-3 top-3 bottom-3 h-auto rounded-xl border border-border/50 shadow-2xl",
                  "w-full p-6 overflow-y-auto",
                  widthClass,
                  "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
                ].join(" "),
            cleanedClassName
          )}
          {...props}
        >
          {children}
          <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPortal>
    );
  }
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

// ─── Header / Footer / Title / Description ──────────────────────────────────

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-left", className)}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
